import { Popover } from "@headlessui/react";
import { rem } from "polished";
import styled from "styled-components";

import { withViewportStyle } from "@/components/editor/shared/with-viewport-style";
import { ColorLabels } from "@/components/editor/utils/use-color";

const AVATAR_SIZE = 40;
const AVATAR_SIZE_SMALL = 32;
const VISIBLE_USERS_COUNT = 5;

interface User {
  firstName: string;
  lastName: string;
  image?: string;
}

interface AvatarGroupProps {
  users: User[];
  visibleUsersCount?: number;
  popupLabel?: string;
  "data-testid"?: string;
}

const Wrapper = withViewportStyle("div")`
  display: flex;
  position: relative;

  ${(props) => props.$viewport.mobile`
    flex-wrap: wrap;
    gap: ${rem(4)};
  `}
`;

const AvatarWrapper = withViewportStyle("div")`
  margin-left: ${rem(-6)};
  flex-shrink: 0;

  &:first-child {
    margin-left: 0;
  }

  ${(props) => props.$viewport.mobile`
    margin-left: 0;
  `}
`;

const AvatarImage = styled.img`
  width: ${rem(AVATAR_SIZE)};
  height: ${rem(AVATAR_SIZE)};
  border-radius: 50%;
  object-fit: cover;
  border: ${rem(2)} solid white;
  background-color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
  flex-shrink: 0;
`;

const AvatarInitials = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(AVATAR_SIZE)};
  height: ${rem(AVATAR_SIZE)};
  border-radius: 50%;
  border: ${rem(2)} solid white;
  background-color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
  color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
  font-size: ${rem(12)};
  font-weight: 600;
  text-transform: uppercase;
  flex-shrink: 0;
`;

const OverflowButton = styled(Popover.Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(AVATAR_SIZE)};
  height: ${rem(AVATAR_SIZE)};
  border-radius: 50%;
  border: ${rem(2)} solid white;
  background-color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
  color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
  font-size: ${rem(12)};
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
`;

const PopoverPanel = styled(Popover.Panel)`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 10;
  margin-top: ${rem(8)};
  padding: ${rem(12)};
  background: white;
  border-radius: ${rem(8)};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: ${rem(200)};
`;

const PopoverList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`;

const PopoverListItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`;

const SmallAvatarImage = styled.img`
  width: ${rem(AVATAR_SIZE_SMALL)};
  height: ${rem(AVATAR_SIZE_SMALL)};
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const SmallAvatarInitials = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(AVATAR_SIZE_SMALL)};
  height: ${rem(AVATAR_SIZE_SMALL)};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
  font-size: ${rem(10)};
  font-weight: 600;
  text-transform: uppercase;
  flex-shrink: 0;
`;

const UserName = styled.span`
  font-size: ${rem(14)};
  color: #333;
  max-width: ${rem(150)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function getInitials(user: User) {
  const first = user.firstName?.charAt(0) || "";
  const last = user.lastName?.charAt(0) || "";
  return `${first}${last}`;
}

export function AvatarGroup({
  users,
  visibleUsersCount = VISIBLE_USERS_COUNT,
  popupLabel,
  "data-testid": testId,
}: AvatarGroupProps) {
  if (!users.length) return null;

  const visibleUsers = users.slice(0, visibleUsersCount);
  const overflowUsers = users.slice(visibleUsersCount);
  const overflowCount = overflowUsers.length;

  return (
    <Wrapper data-testid={testId}>
      {visibleUsers.map((user, index) => (
        <AvatarWrapper key={`${user.firstName}-${user.lastName}-${index}`}>
          {user.image ? (
            <AvatarImage
              src={user.image}
              alt={`${user.firstName} ${user.lastName}`}
            />
          ) : (
            <AvatarInitials aria-label={`${user.firstName} ${user.lastName}`}>
              {getInitials(user)}
            </AvatarInitials>
          )}
        </AvatarWrapper>
      ))}

      {overflowCount > 0 && (
        <AvatarWrapper>
          <Popover>
            <OverflowButton aria-label={popupLabel}>
              +{overflowCount}
            </OverflowButton>

            <PopoverPanel>
              <PopoverList>
                {overflowUsers.map((user, index) => (
                  <PopoverListItem
                    key={`overflow-${user.firstName}-${user.lastName}-${index}`}
                  >
                    {user.image ? (
                      <SmallAvatarImage
                        src={user.image}
                        alt={`${user.firstName} ${user.lastName}`}
                      />
                    ) : (
                      <SmallAvatarInitials>
                        {getInitials(user)}
                      </SmallAvatarInitials>
                    )}
                    <UserName>
                      {user.firstName} {user.lastName}
                    </UserName>
                  </PopoverListItem>
                ))}
              </PopoverList>
            </PopoverPanel>
          </Popover>
        </AvatarWrapper>
      )}
    </Wrapper>
  );
}
